import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Clock, Send, X, AlertTriangle, CheckCircle2, Ban, RefreshCw, Play, Eye } from "lucide-react";
import DevicePreviewIframe from "../components/email/DevicePreviewIframe";

const STATUS_META = {
  pending: { label: "Pendiente", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: Clock },
  sent: { label: "Enviado", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  failed: { label: "Fallido", cls: "bg-rose-50 text-rose-800 border-rose-200", icon: AlertTriangle },
  cancelled: { label: "Cancelado", cls: "bg-gray-100 text-gray-700 border-gray-300", icon: Ban },
};

export default function AdminEmailSchedulerPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [ticking, setTicking] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmCancelId, setConfirmCancelId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/email-scheduler/queue?status_=${statusFilter}&limit=200`);
      setItems(data.items || []);
      const s = await api.get("/admin/email-scheduler/stats");
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando queue");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const forceTick = async () => {
    setTicking(true);
    try {
      const { data } = await api.post("/admin/email-scheduler/tick");
      toast.success(`Tick: ${data.sent} enviados · ${data.processed} procesados · ${data.errors} errores`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error en tick");
    } finally {
      setTicking(false);
    }
  };

  const openDetail = async (item) => {
    try {
      const { data } = await api.get(`/admin/email-scheduler/queue/${item.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando detalle");
    }
  };

  const cancelItem = async (id) => {
    try {
      await api.post(`/admin/email-scheduler/queue/${id}/cancel`);
      toast.success("Email cancelado");
      setDetail(null);
      setConfirmCancelId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al cancelar");
    }
  };

  const fmtRel = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.round((d - now) / 60000);
    if (Math.abs(diffMin) < 60) {
      return diffMin >= 0 ? `en ${diffMin}m` : `hace ${-diffMin}m`;
    }
    if (Math.abs(diffMin) < 60 * 24) {
      const h = Math.round(diffMin / 60);
      return h >= 0 ? `en ${h}h` : `hace ${-h}h`;
    }
    return d.toLocaleString("es-CO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="container-prose py-8" data-testid="admin-email-scheduler-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-brand-burgundy font-medium mb-1">Email Scheduler</p>
          <h1 className="font-serif text-2xl text-gray-900">Cola de envíos diferidos</h1>
          <p className="text-xs text-gray-500 mt-1">Emails programados (delay 15min tras estado clave del caso).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-sm" data-testid="scheduler-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refrescar
          </Button>
          <Button onClick={forceTick} disabled={ticking} className="rounded-sm bg-brand-burgundy hover:bg-brand-burgundy-light text-white" data-testid="scheduler-force-tick">
            <Play className="w-4 h-4 mr-1.5" /> {ticking ? "Procesando…" : "Force tick"}
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="scheduler-stats">
          {["pending", "sent", "failed", "cancelled"].map((k) => {
            const meta = STATUS_META[k];
            const Icon = meta.icon;
            return (
              <button
                key={k}
                onClick={() => setStatusFilter(k)}
                className={`text-left p-3 border rounded-sm transition-colors ${
                  statusFilter === k ? `${meta.cls} border-2` : "bg-white border-gray-200 hover:border-gray-400"
                }`}
                data-testid={`scheduler-filter-${k}`}
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gray-500">
                  <Icon className="w-3.5 h-3.5" />
                  {meta.label}
                </div>
                <div className="text-2xl font-serif text-gray-900 mt-1 tabular-nums">{stats.counts[k] || 0}</div>
              </button>
            );
          })}
          <div className="p-3 border border-brand-burgundy/30 bg-brand-cream-soft rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-brand-burgundy">Próximos 30min</div>
            <div className="text-2xl font-serif text-brand-burgundy mt-1 tabular-nums">{stats.due_in_30min}</div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="scheduler-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-[10px] uppercase tracking-widest text-gray-500">
              <th className="text-left px-4 py-2.5 font-medium">Slug</th>
              <th className="text-left px-4 py-2.5 font-medium">Destinatario</th>
              <th className="text-left px-4 py-2.5 font-medium">Programado</th>
              <th className="text-left px-4 py-2.5 font-medium">Intentos</th>
              <th className="text-right px-4 py-2.5 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-gray-400 py-12 italic">
                  Sin emails en {STATUS_META[statusFilter]?.label.toLowerCase() || statusFilter}.
                </td>
              </tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors" data-testid={`scheduler-row-${it.id}`}>
                <td className="px-4 py-2.5">
                  <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded-sm">{it.slug}</code>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{it.to_email}</td>
                <td className="px-4 py-2.5 text-xs">
                  <div className="text-gray-900">{fmtRel(it.scheduled_at)}</div>
                  <div className="text-[10px] text-gray-400">{new Date(it.scheduled_at).toLocaleString("es-CO")}</div>
                </td>
                <td className="px-4 py-2.5 text-xs tabular-nums">
                  {it.attempts || 0}{it.last_error && <span className="text-rose-600 ml-1" title={it.last_error}>!</span>}
                </td>
                <td className="px-4 py-2.5 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openDetail(it)} className="text-gray-700" data-testid={`scheduler-detail-${it.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                  </Button>
                  {it.status === "pending" && (
                    <Button size="sm" variant="ghost" onClick={() => setConfirmCancelId(it.id)} className="text-rose-700 hover:bg-rose-50" data-testid={`scheduler-cancel-${it.id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Cancelar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="scheduler-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-brand-burgundy" />
              Email programado · <code className="text-sm font-mono">{detail?.item?.slug}</code>
            </DialogTitle>
            <DialogDescription>
              {detail && `Para ${detail.item.to_email} — ${fmtRel(detail.item.scheduled_at)}`}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3 text-xs bg-gray-50 p-3 border border-gray-200 rounded-sm">
                <Kv k="Estado" v={detail.item.status} />
                <Kv k="Programado para" v={new Date(detail.item.scheduled_at).toLocaleString("es-CO")} />
                <Kv k="Creado" v={new Date(detail.item.created_at).toLocaleString("es-CO")} />
                <Kv k="Intentos" v={String(detail.item.attempts || 0)} />
                {detail.item.dedup_key && <Kv k="Dedup key" v={detail.item.dedup_key} />}
                {detail.item.last_error && <Kv k="Último error" v={detail.item.last_error} />}
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Contexto (variables)</p>
                <pre className="text-[11px] bg-gray-100 p-3 rounded-sm overflow-x-auto max-h-[160px]">
                  {JSON.stringify(detail.item.context, null, 2)}
                </pre>
              </div>

              {detail.preview_html && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Preview renderizado</p>
                  <div className="border border-gray-200 rounded-sm p-3 bg-white">
                    <p className="text-xs text-gray-700 mb-2"><strong>Asunto:</strong> {detail.preview_subject}</p>
                    <DevicePreviewIframe srcDoc={detail.preview_html} testIdPrefix="scheduler-detail-preview" height={420} />
                  </div>
                </div>
              )}

              {detail.item.status === "pending" && (
                <div className="flex justify-end">
                  <Button onClick={() => setConfirmCancelId(detail.item.id)} variant="outline" className="text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="scheduler-detail-cancel">
                    <X className="w-4 h-4 mr-1.5" /> Cancelar envío
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter23 — Shadcn confirm dialog (reemplaza window.confirm) */}
      <Dialog open={!!confirmCancelId} onOpenChange={(o) => !o && setConfirmCancelId(null)}>
        <DialogContent className="max-w-md" data-testid="scheduler-cancel-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-800">
              <X className="w-5 h-5" />
              ¿Cancelar este email?
            </DialogTitle>
            <DialogDescription>
              El email programado no se enviará. Esta acción no se puede deshacer (puedes encolar uno nuevo manualmente si lo necesitas).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setConfirmCancelId(null)} data-testid="scheduler-cancel-dismiss">
              Mantener
            </Button>
            <Button
              variant="outline"
              className="text-rose-700 border-rose-300 hover:bg-rose-50"
              onClick={() => cancelItem(confirmCancelId)}
              data-testid="scheduler-cancel-confirm"
            >
              Sí, cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{k}</div>
      <div className="text-xs text-gray-800 truncate">{v || "—"}</div>
    </div>
  );
}
